import "./AnalysisDetailForm.css";
import "../JobDetailForm/JobDetailForm.css";

import ExperienceDetailForm from "../ExperienceDetailForm/ExperienceDetailForm";
import EducationDetailForm from "../EducationDetailForm/EducationDetailForm";
import LoadingOverlay from "../LoadingOverlay/LoadingOverlay";

import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button, Form } from "react-bootstrap";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import { useCookies } from "react-cookie";
// import { el } from "date-fns/locale";

const AnalysisDetailForm = ({
    analysisId = null,
    analysis_object = null,
    closeModal = null,
    refreshData = null,
    initialTabIndex = 0
}) => {
    const [analysis, setAnalysis] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [cookies] = useCookies(["AuthorizationToken"]);
    const experienceRef = useRef({});
    const educationRef = useRef({});
    const [activeTab, setActiveTab] = useState(initialTabIndex);
    const [showNewExperienceForm, setShowNewExperienceForm] = useState(false);
    const [showNewEducationForm, setShowNewEducationForm] = useState(false);
    const [experienceCounter, setExperienceCounter] = useState(0);
    const [educationCounter, setEducationCounter] = useState(0);

    useEffect(() => {
        const fetchAnalysisDetails = async () => {
            setIsLoading(true);
            try {
                const response = await axios.get(
                    `https://api.foveaai.com/evaluate/web/analyses/${analysisId}`,
                    {
                        headers: { Authorization: cookies.AuthorizationToken },
                    }
                );
                const analysisData =
                    typeof response.data === "string"
                        ? JSON.parse(response.data)
                        : response.data;
                setAnalysis(analysisData);
            } catch (error) {
                console.error("Error fetching analysis details:", error);
            } finally {
                setIsLoading(false);
            }
        };

        if (analysis_object == null) {
            fetchAnalysisDetails();
        } else {
            setAnalysis(analysis_object);
        }
    }, [analysisId, analysis_object, cookies.AuthorizationToken]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsLoading(true);

        if (analysis && analysis.id) {
            // Perform the candidate fields update
            const candidate_data = {
                first_name: analysis.first_name,
                last_name: analysis.last_name,
                locality: analysis.locality,
                administrative_area: analysis.administrative_area,
                postal_code: analysis.postal_code,
                email: analysis.email,
                phone: analysis.phone,
                skill_set: analysis.skill_set,
                primary_job_title: analysis.primary_job_title,
            };

            // Loop through the experiences and update or add them
            const experiencePromises = Object.entries(experienceRef.current).map(
                ([key, expRef]) => {
                    // Check if expRef is a valid object and has the necessary methods
                    if (expRef && typeof expRef === 'object' && 'isNewEntry' in expRef) {
                        if (expRef.isNewEntry === true) {
                            console.log("Processing new experience:", key);
                            return expRef.handleAddExperience().then(response => {
                                return response;
                            });
                        } else {
                            console.log("Processing existing experience:", key);
                            return expRef.handleUpdateExperience().then(response => {
                                return response;
                            });
                        }
                    }
                    console.log("Skipping invalid experience reference:", key);
                    return Promise.resolve(); // Skip if ref is not properly set
                }
            );

            const educationPromises = Object.entries(educationRef.current).map(
                ([key, eduRef]) => {
                    // Check if eduRef is a valid object and has the necessary methods
                    if (eduRef && typeof eduRef === 'object' && 'isNewEntry' in eduRef) {
                        if (eduRef.isNewEntry === true) {
                            console.log("Processing new education:", key);
                            return eduRef.handleAddEducation().then(response => {
                                return response;
                            });
                        } else {
                            console.log("Processing existing education:", key);
                            return eduRef.handleUpdateEducation().then(response => {
                                return response;
                            });
                        }
                    }
                    console.log("Skipping invalid education reference:", key);
                    return Promise.resolve(); // Skip if ref is not properly set
                }
            );

            try {
                try {
                    await axios.put(
                        `https://api.foveaai.com/evaluate/web/candidates/${analysis.analysis.candidate_id}`,
                        candidate_data,
                        {
                            headers: {
                                Authorization: cookies.AuthorizationToken,
                            },
                        }
                    );
                } catch (error) {
                    console.error("Error updating candidate fields:", error);
                }

                // Perform the analysis fields update
                const analysis_data = {
                    job_fit_rating_normalized:
                        analysis.analysis.job_fit_rating_normalized,
                    candidate_summary: analysis.analysis.candidate_summary,
                    job_fit_narrative: analysis.analysis.job_fit_narrative,
                    interview_questions: analysis.analysis.interview_questions,
                    notes: analysis.analysis.notes,
                    url: analysis.analysis.url,
                    url_anchor_text: analysis.analysis.url_anchor_text,
                };

                try {
                    await axios.put(
                        `https://api.foveaai.com/evaluate/web/analyses/${analysis.analysis.id}`,
                        analysis_data,
                        {
                            headers: {
                                Authorization: cookies.AuthorizationToken,
                            },
                        }
                    );
                } catch (error) {
                    console.error("Error updating analysis fields:", error);
                }

                try {
                    // Perform all of the experience updates
                    await Promise.all(experiencePromises);
                    
                    // Close the new experience form
                    setShowNewExperienceForm(false);

                    // Reset the counter
                    setExperienceCounter(0);
                } catch (error) {
                    console.error("Error updating or adding experience fields:", error);
                }

                try {
                    // Perform all of the education updates
                    await Promise.all(educationPromises);

                    // Close the new education form
                    setShowNewEducationForm(false);

                    // Reset the counter
                    setEducationCounter(0);
                } catch (error) {
                    console.error("Error updating or adding education fields:", error);
                }
            } finally {
                // Return even if there is an error
                if (refreshData) {
                    refreshData();
                }
                setIsLoading(false);
                if (closeModal) {
                    closeModal();
                }
            }
        }
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setAnalysis((prevAnalysis) => {
            const keys = name.split(".");
            let lastRef = prevAnalysis;
            keys.slice(0, -1).forEach((key) => {
                if (!lastRef[key]) lastRef[key] = {}; // Initialize if does not exist
                lastRef = lastRef[key];
            });
            lastRef[keys[keys.length - 1]] = value;
            return { ...prevAnalysis };
        });
    };

    // Function to handle experience deletion
    const handleExperienceDelete = (deletedExperienceId) => {
        setAnalysis(prevAnalysis => ({
            ...prevAnalysis,
            experiences: prevAnalysis.experiences.filter(exp => exp.id !== deletedExperienceId)
        }));
        // Ensure the experience tab (index 3) remains selected after deletion
        setActiveTab(3);

        // Call refreshData to update parent component
        if (refreshData) {
            refreshData();
        }
    };

    // Function to handle education deletion
    const handleEducationDelete = (deletedEducationId) => {
        setAnalysis(prevAnalysis => ({
            ...prevAnalysis,
            educations: prevAnalysis.educations.filter(edu => edu.id !== deletedEducationId)
        }));
        // Ensure the education tab (index 4) remains selected after deletion
        setActiveTab(4);

        // Call refreshData to update parent component
        if (refreshData) {
            refreshData();
        }
    };

    // Function to handle tab selection
    const handleTabSelect = (index) => {
        setActiveTab(index);
    };

    const handleAddExperience = () => {
        setShowNewExperienceForm(true);
        setExperienceCounter(prev => prev + 1);
    };

    const handleAddEducation = () => {
        setShowNewEducationForm(true);
        setEducationCounter(prev => prev + 1);
    };

    const handleNewExperienceAdd = (newExperience) => {
        // Add the new experience to the analysis state
        console.log("Adding new experience to form list:", newExperience);

        setAnalysis(prevAnalysis => ({
            ...prevAnalysis,
            experiences: [...prevAnalysis.experiences, newExperience]
        }));

        setShowNewExperienceForm(false);
    
        // Ensure the experience tab (index 3) remains selected after deletion
        setActiveTab(3);

        // Call refreshData to update parent component
        if (refreshData) {
            refreshData();
        }
    };

    const handleNewEducationAdd = (newEducation) => {
        // Add the new education to the analysis state
        console.log("Adding new education to form list:", newEducation);

        setAnalysis(prevAnalysis => ({
            ...prevAnalysis,
            educations: [...prevAnalysis.educations, newEducation]
        }));

        setShowNewEducationForm(false);

        // Ensure the education tab (index 4) remains selected after deletion
        setActiveTab(4);

        // Call refreshData to update parent component
        if (refreshData) {
            refreshData();
        }
    };

    // Derived UI state for checklist completion
    const checklist = {
        candidate: Boolean((analysis?.first_name || '').trim()) && Boolean((analysis?.last_name || '').trim()),
        evaluation: Boolean((analysis?.primary_job_title || analysis?.skill_set || analysis?.analysis?.candidate_summary || '').trim()),
        notes: Boolean((analysis?.analysis?.notes || analysis?.analysis?.url || '').trim()),
        experience: (analysis?.experiences?.length || 0) > 0,
        education: (analysis?.educations?.length || 0) > 0,
    };

    const progressPct = ((activeTab + 1) / 5) * 100;

    return (
        <div className="job-detail-container">
            {isLoading && <LoadingOverlay />}
            <div className="job-modal">
                <div className="job-modal-header">
                    <div className="header-left">
                        <div className="job-modal-title">Edit Evaluation</div>
                    </div>
                    {closeModal && (
                        <button type="button" className="close-btn" onClick={closeModal} aria-label="Close">✕</button>
                    )}
                </div>
                <div className="job-modal-progress">
                    <div className="progress-fill" style={{ width: `${progressPct}%` }} />
                </div>

                <Form onSubmit={handleSubmit}>
                    <div className="job-modal-body">
                        <div className="job-main-content">
                            <Tabs
                                selectedIndex={activeTab}
                                onSelect={handleTabSelect}
                                forceRenderTabPanel={true}
                            >
                                <TabList className="job-tabs">
                                    <Tab className="job-tab">Profile</Tab>
                                    <Tab className="job-tab">Evaluation</Tab>
                                    <Tab className="job-tab">Notes</Tab>
                                    <Tab className="job-tab">Experience</Tab>
                                    <Tab className="job-tab">Education</Tab>
                                </TabList>

                                <TabPanel>
                                    <div className="tab-content">
                                        <div className="quick-tips">
                                            <div className="tips-icon">👤</div>
                                            <div className="tips-content">
                                                <div className="tips-title">Keep candidate info current</div>
                                                <div className="tips-text">Adjust candidate name, contact or location information.</div>
                                            </div>
                                        </div>

                                        <div className="form-group">
                                            <Form.Label htmlFor="first_name" className="modern-label">First Name</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="first_name"
                                                id="first_name"
                                                value={analysis?.first_name ?? ""}
                                                onChange={handleInputChange}
                                                className="modern-input"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <Form.Label htmlFor="last_name" className="modern-label">Last Name</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="last_name"
                                                id="last_name"
                                                value={analysis?.last_name ?? ""}
                                                onChange={handleInputChange}
                                                className="modern-input"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <Form.Label htmlFor="locality" className="modern-label">City</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="locality"
                                                id="locality"
                                                value={analysis?.locality ?? ""}
                                                onChange={handleInputChange}
                                                className="modern-input"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <Form.Label htmlFor="administrative_area" className="modern-label">State/Province</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="administrative_area"
                                                id="administrative_area"
                                                value={analysis?.administrative_area ?? ""}
                                                onChange={handleInputChange}
                                                className="modern-input"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <Form.Label htmlFor="postal_code" className="modern-label">Postal Code</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="postal_code"
                                                id="postal_code"
                                                value={analysis?.postal_code ?? ""}
                                                onChange={handleInputChange}
                                                className="modern-input"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <Form.Label htmlFor="email" className="modern-label">Email</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="email"
                                                id="email"
                                                value={analysis?.email ?? ""}
                                                onChange={handleInputChange}
                                                className="modern-input"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <Form.Label htmlFor="phone" className="modern-label">Phone</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="phone"
                                                id="phone"
                                                value={analysis?.phone ?? ""}
                                                onChange={handleInputChange}
                                                className="modern-input"
                                            />
                                        </div>
                                    </div>
                                </TabPanel>

                                <TabPanel>
                                    <div className="tab-content">
                                        <div className="quick-tips">
                                            <div className="tips-icon">📝</div>
                                            <div className="tips-content">
                                                <div className="tips-title">Evaluation results</div>
                                                <div className="tips-text">Modify summaries and narratives to help communicate strengths and fit.</div>
                                            </div>
                                        </div>

                                        <div className="form-group">
                                            <Form.Label htmlFor="primary_job_title" className="modern-label">Primary Job Title</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="primary_job_title"
                                                id="primary_job_title"
                                                value={analysis?.primary_job_title ?? ""}
                                                onChange={handleInputChange}
                                                className="modern-input"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <Form.Label htmlFor="skill_set" className="modern-label">Skill Set</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                name="skill_set"
                                                id="skill_set"
                                                value={analysis?.skill_set ?? ""}
                                                onChange={handleInputChange}
                                                className="modern-textarea"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <Form.Label htmlFor="candidate_summary" className="modern-label">Candidate Summary</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={5}
                                                name="analysis.candidate_summary"
                                                id="candidate_summary"
                                                value={analysis?.analysis?.candidate_summary ?? ""}
                                                onChange={handleInputChange}
                                                className="modern-textarea"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <Form.Label htmlFor="job_fit_narrative" className="modern-label">Strengths & Weaknesses</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={5}
                                                name="analysis.job_fit_narrative"
                                                id="job_fit_narrative"
                                                value={analysis?.analysis?.job_fit_narrative ?? ""}
                                                onChange={handleInputChange}
                                                className="modern-textarea"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <Form.Label htmlFor="interview_questions" className="modern-label">Interview Questions</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={5}
                                                name="analysis.interview_questions"
                                                id="interview_questions"
                                                value={analysis?.analysis?.interview_questions ?? ""}
                                                onChange={handleInputChange}
                                                className="modern-textarea"
                                            />
                                        </div>
                                    </div>
                                </TabPanel>

                                <TabPanel>
                                    <div className="tab-content">
                                        <div className="quick-tips">
                                            <div className="tips-icon">✍️</div>
                                            <div className="tips-content">
                                                <div className="tips-title">Add helpful notes</div>
                                                <div className="tips-text">Links can be included to external materials relevant to the candidate.</div>
                                            </div>
                                        </div>

                                        <div className="form-group">
                                            <Form.Label htmlFor="notes" className="modern-label">Notes</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                name="analysis.notes"
                                                id="notes"
                                                value={analysis?.analysis?.notes ?? ""}
                                                onChange={handleInputChange}
                                                className="modern-textarea"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <Form.Label htmlFor="url" className="modern-label">Additional Info Link URL</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="analysis.url"
                                                id="url"
                                                value={analysis?.analysis?.url ?? ""}
                                                onChange={handleInputChange}
                                                className="modern-input"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <Form.Label htmlFor="url_anchor_text" className="modern-label">Additional Info Link Text</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="analysis.url_anchor_text"
                                                id="url_anchor_text"
                                                value={analysis?.analysis?.url_anchor_text ?? ""}
                                                onChange={handleInputChange}
                                                className="modern-input"
                                            />
                                        </div>
                                    </div>
                                </TabPanel>

                                <TabPanel>
                                    <div className="tab-content">
                                        {/* Quick tips for Experience */}
                                        <div className="quick-tips">
                                            <div className="tips-icon">💼</div>
                                            <div className="tips-content">
                                                <div className="tips-title">Set job experience</div>
                                                <div className="tips-text">Add or remove job experience to reflect the candidate's qualifications.</div>
                                            </div>
                                        </div>

                                        <div className="analysis-detail-form-section-heading">
                                            <h2>Candidate Experience</h2>
                                        </div>
                                        {analysis?.experiences?.map((experience) => (
                                            <div className="experience-detail-form-groups" key={experience.id}>
                                                <ExperienceDetailForm
                                                    ref={(el) => (experienceRef.current[experience.id] = el)}
                                                    experience_object={experience}
                                                    experience_id={experience.id}
                                                    candidate_id={experience.candidate_id}
                                                    onDelete={handleExperienceDelete}
                                                />
                                            </div>
                                        ))}
                                        {showNewExperienceForm && (
                                            <div className="experience-detail-form-groups">
                                                <ExperienceDetailForm
                                                    ref={(el) => (experienceRef.current[`new_${experienceCounter}`] = el)}
                                                    candidate_id={analysis.analysis.candidate_id}
                                                    isNewEntry={true}
                                                    onCancelAdd={() => setShowNewExperienceForm(false)}
                                                    onAdd={handleNewExperienceAdd}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </TabPanel>

                                <TabPanel>
                                    <div className="tab-content">
                                        {/* Quick tips for Education */}
                                        <div className="quick-tips">
                                            <div className="tips-icon">🎓</div>
                                            <div className="tips-content">
                                                <div className="tips-title">Set education and certifications</div>
                                                <div className="tips-text">Add or remove candidate education like degrees, certifications, training and more.</div>
                                            </div>
                                        </div>

                                        <div className="analysis-detail-form-section-heading">
                                            <h2>Candidate Education</h2>
                                        </div>
                                        {analysis?.educations?.map((education) => (
                                            <div className="education-detail-form-groups" key={education.id}>
                                                <EducationDetailForm
                                                    ref={(el) => (educationRef.current[education.id] = el)}
                                                    education_object={education}
                                                    education_id={education.id}
                                                    candidate_id={education.candidate_id}
                                                    onDelete={handleEducationDelete}
                                                />
                                            </div>
                                        ))}
                                        {showNewEducationForm && (
                                            <div className="education-detail-form-groups">
                                                <EducationDetailForm
                                                    ref={(el) => (educationRef.current[`new_${educationCounter}`] = el)}
                                                    candidate_id={analysis.analysis.candidate_id}
                                                    isNewEntry={true}
                                                    onCancelAdd={() => setShowNewEducationForm(false)}
                                                    onAdd={handleNewEducationAdd}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </TabPanel>
                            </Tabs>
                        </div>

                        <aside className="job-side-panel">
                            <div className="side-panel-title">Checklist</div>
                            <div className="checklist">
                                <div className={`checklist-item ${checklist.candidate ? "completed" : ""}`}>
                                    <div className="checklist-checkbox" />
                                    Profile
                                </div>
                                <div className={`checklist-item ${checklist.evaluation ? "completed" : ""}`}>
                                    <div className="checklist-checkbox" />
                                    Evaluation
                                </div>
                                <div className={`checklist-item ${checklist.notes ? "completed" : ""}`}>
                                    <div className="checklist-checkbox" />
                                    Notes
                                </div>
                                <div className={`checklist-item ${checklist.experience ? "completed" : ""}`}>
                                    <div className="checklist-checkbox" />
                                    Experience
                                </div>
                                <div className={`checklist-item ${checklist.education ? "completed" : ""}`}>
                                    <div className="checklist-checkbox" />
                                    Education
                                </div>
                            </div>
                            <a
                                href="https://foveaai.com/ai-recruiting-resources-and-guides-foveaai/"
                                target="_blank"
                                rel="noreferrer"
                                className="best-practices-link"
                            >
                                Tutorials
                            </a>
                        </aside>
                    </div>

                    <div className="job-modal-footer">
                        {closeModal && (
                            <Button
                                variant="secondary"
                                onClick={closeModal}
                                className="btn-modern-secondary"
                                type="button"
                            >
                                Cancel
                            </Button>
                        )}
                        {activeTab === 3 && (
                            <Button
                                variant="secondary"
                                onClick={handleAddExperience}
                                className="btn-modern-secondary"
                                type="button"
                            >
                                Add Experience
                            </Button>
                        )}
                        {activeTab === 4 && (
                            <Button
                                variant="secondary"
                                onClick={handleAddEducation}
                                className="btn-modern-secondary"
                                type="button"
                            >
                                Add Education
                            </Button>
                        )}
                        <Button
                            variant="success"
                            type="submit"
                            className="btn-modern-primary"
                        >
                            Save All Changes
                        </Button>
                    </div>
                </Form>
            </div>
        </div>
    );
};

export default AnalysisDetailForm;
